import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material/styles';
import { Theme } from '@mui/material/styles/createTheme';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert } from 'src/components/Alert';
import { BackgroundGradient } from 'src/components/BackgroundGradient';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Container } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { resetUserPassword } from 'src/models/user/user';
import toast from 'src/utils/toast';

const BackgroundWrapper = styled('div')(() => ({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '100%',
  height: '100%',
  zIndex: 0,
}));

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('translation', { keyPrefix: 'session' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { user, userLoading, session } = useOptionalSession();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redirectPath, setRedirectPath] = useState('/embed/dashboard');

  useEffect(() => {
    const state = location.state as {
      unauthorizedMessage?: string;
      from?: string;
    };

    if (state) {
      if (state.unauthorizedMessage) setError(state.unauthorizedMessage);
      if (state.from) setRedirectPath(state.from);

      // Clear the state to prevent showing the message on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!userLoading && user?.id) {
      navigate(redirectPath, { replace: true });
    }
  }, [user, userLoading, navigate, redirectPath]);

  const handleReset = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setLoading(true);

    try {
      const response = await resetUserPassword(email);
      if (response.success == 1) {
        toast.success(tCommon('messages.passwordResetEmailSent'));
      } else {
        setError(tCommon('messages.errorResettingPassword'));
      }
      setLoading(false);
    } catch (e) {
      const error = e as any;
      if (error?.response?.data?.success) {
        setError(error?.response?.data?.message);
      } else {
        setError(tCommon('messages.errorResettingPassword'));
      }
      setLoading(false);
    }
  };

  const signInDisabled = !email;

  return (
    <DrawerLayout drawerOpen={false}>
      <BackgroundWrapper>
        <BackgroundGradient color={error ? 'error' : 'primary'} />
      </BackgroundWrapper>

      <Container
        maxWidth="lg"
        sx={(theme: Theme) => ({
          [theme.breakpoints.down('md')]: {
            mb: 4,
            mt: 4,
          },
          mb: 8,
          mt: 8,
          position: 'relative',
        })}
      >
        <Stack
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4" mb={1}>
            {t('resetPassword')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {session?.brand}
          </Typography>
        </Stack>
      </Container>
      <Container maxWidth="sm" sx={{ position: 'relative' }}>
        <Card sx={{ p: 2.5 }}>
          <Collapse in={!!error} collapsedSize={0}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </Collapse>

          <form onSubmit={handleReset}>
            <TextField
              sx={{ mt: 0 }}
              label={tCommon('labels.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Button
              variant="contained"
              sx={{ mt: 3 }}
              disabled={signInDisabled || loading}
              onClick={handleReset}
              loading={loading}
              type="submit"
              fullWidth
            >
              {t('resetPassword')}
            </Button>
          </form>
        </Card>

        {session?.freeSignups && (
          <Typography
            sx={{ mt: 5, position: 'relative' }}
            align="center"
            variant="body1"
            color="text.secondary"
          >
            {tCommon('messages.dontHaveAccount')}
            <br />
            <Link component={RouterLink} to={`/sign-up/`}>
              {t('clickHere')}
            </Link>
            &nbsp;{t('toSignUp')}
          </Typography>
        )}
      </Container>
    </DrawerLayout>
  );
};
